<template>
    <basic-header pretitle="Account" title="Entitlement Usage" :compact="true" />
    <div class="container-fluid">
        <tabs :tabs="tabItems">
            <template v-slot:monthly>
                <div class="row g-3 align-items-center mt-3">
                    <div class="col-auto">
                        <InputField colField="mb-0"  class="h2" :editable="true" label="Usage Data for Month" :options="monthOptions"
                            type="select" :modelValue="usageMonth" @change="usageMonthChange($event)" />
                    </div>
                </div>
                <div v-if="loading" class="usage-loading text-center text-body-secondary">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mb-0 mt-3">Loading usage&hellip;</p>
                </div>
                <template v-else>
                    <EntitlementUsageCharts v-if="type=='Monthly' && hasMonthlyUsage" :chartValue="entitlementUsage" :type="type"/>
                    <div v-else-if="type=='Monthly'" class="usage-empty text-center text-body-secondary">
                        <i class="fa fa-chart-column usage-empty__icon"></i>
                        <p class="mb-0">No usage recorded for this period.</p>
                    </div>
                </template>
            </template>
            <template v-slot:historical>
                <div v-if="loading" class="usage-loading text-center text-body-secondary">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mb-0 mt-3">Loading usage&hellip;</p>
                </div>
                <template v-else>
                    <HistoricalEntitlementUsageCharts v-if="type=='Historical' && hasHistoricalUsage" :monthlyData="historicalEntitlementUsage" :type="type"/>
                    <div v-else-if="type=='Historical'" class="usage-empty text-center text-body-secondary">
                        <i class="fa fa-chart-column usage-empty__icon"></i>
                        <p class="mb-0">No usage recorded over the last six months.</p>
                    </div>
                </template>
            </template>
        </tabs>
    </div>
</template>

<script>
import axios from "axios";

import dayjs from "dayjs";
import BasicHeader from "../../components/BasicHeader.vue";
import InputField from "../../components/InputField.vue";
import Tabs from "../../components/Tabs.vue";
import { mapState } from "vuex";
import { generateLastSixMonthOptions , getFirstDayOfMonth, getLastDayOfMonth} from '../../utils/commonFunction';
import EntitlementUsageCharts from "../../components/admin/EntitlementUsageCharts.vue";
import HistoricalEntitlementUsageCharts from "../../components/admin/HistoricalEntitlementUsageCharts.vue";
export default {
    components: { BasicHeader, InputField, Tabs, EntitlementUsageCharts, HistoricalEntitlementUsageCharts },
    data() {
        return {
            loading: false,
            countsData: [],
            entitlements: [],
            entitlementUsage: [],
            historicalEntitlementUsage: [],
            dateConfig: {
                wrap: true,
                dateFormat: 'Y-m-d'
            },
            startDate: null,
            endDate: null,
            snapType: 'Monthly',
            monthOptions:generateLastSixMonthOptions(false)      
        }
    },    
    computed: {      
        ...mapState({
            usageMonth: state => state.admin.entitlementUsageMonth,
        }),
        hasMonthlyUsage() {
            return Array.isArray(this.entitlementUsage)
                && this.entitlementUsage.some(item => item && item.consumption > 0);
        },
        hasHistoricalUsage() {
            return Object.values(this.historicalEntitlementUsage || {})
                .some(monthData => Array.isArray(monthData)
                    && monthData.some(item => item && item.consumption > 0));
        },
        startDateLabel() {
            return dayjs(this.startDate).format("MMM. D")
        },
        endDateLabel() {
            return dayjs(this.endDate).format("MMM. D, YYYY") 
        },
        tabItems() {
            var self= this
            return [
                {
                    name: "Monthly",
                    id: "id1",
                    slot: "monthly",
                    onClick: function(){
                        self.getCurrentEntitlementUsage()
                    }
                },
                {
                    name: "Historical",
                    id: "id2",
                    slot: "historical",
                    onClick: function(){
                        self.getHistoricalEntitlementUsage()
                    }
                },
                {
                    name: "aUT",
                    id: "id3",
                    slot: "aut",
                    hide:  true
                },
                {
                    name: "aUT Legacy",
                    id: "id1",
                    slot: "aut-unpooled",
                    hide:  true
                },

            ]

        },
        type(){
            return this.$store.state.activeTab || 'Monthly'
        }
    },
    methods: {
        async usageMonthChange(month) {
            this.$store.dispatch('admin/setEntitlementUsageMonth',month);            
            this.startDate = getFirstDayOfMonth(month);
            this.endDate = getLastDayOfMonth(month);
            await this.getCurrentEntitlementUsage();            
        },    
        async initChartData() {
            if(this.type =='Monthly')
                await this.getCurrentEntitlementUsage()
            else
                await this.getHistoricalEntitlementUsage()
        },
        async fetchEntitlementUsage(startDate, endDate) {
            var entitlementUsage = []
            entitlementUsage = await axios.get(
                `${this.$store.state.clientMgmtUrl}/v1/clients/${this.$store.state.currentClient.cid}/product_entitlement/dashboard?start_date=${startDate}&end_date=${endDate}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                    },
                }
            );
            return entitlementUsage.data ? entitlementUsage.data:[]
            
        },
        async getCurrentEntitlementUsage(dateChange=false) {            
            
            this.loading = true;
            this.entitlementUsage = [];
            this.entitlementUsage = await this.fetchEntitlementUsage(this.startDate, this.endDate)

            this.loading = false;
            this.snapType= this.type
        },

        async getHistoricalEntitlementUsage(dateChange=false) {
            this.loading = true;
            this.historicalEntitlementUsage = [];
            let historicalEntitlementUsage = [];
            
            const monthSpan = 6
            for (let i=monthSpan-1; i>-1; i--){
                let dateObj = dayjs().subtract(i, "month");
                let startDate = dateObj.startOf('month').format('YYYY-MM-DD');
                let endDate = dateObj.endOf('month').format('YYYY-MM-DD');
                let month = dateObj.format('MMMM');

                historicalEntitlementUsage[month] = await this.fetchEntitlementUsage(startDate, endDate)
            }


            this.historicalEntitlementUsage = historicalEntitlementUsage

            this.loading = false;
            this.snapType= this.type
        },
    },
    beforeMount() {
        const month =  this.usageMonth;
        this.startDate = getFirstDayOfMonth(month);
        this.endDate = getLastDayOfMonth(month);
    },
    async mounted() {
        this.$store.dispatch('setActiveTab', "Monthly")
        await this.initChartData();
        this.snapType= this.type;
    }
};
</script>

<style scoped>
.usage-empty,
.usage-loading {
    padding: 5rem 1rem;
}

.usage-empty__icon {
    display: block;
    font-size: 2.5rem;
    opacity: 0.35;
    margin-bottom: 1rem;
}
</style>